import styled from "styled-components";
import {
  Aggregator,
  ArrowDown,
  ArrowLength,
  ArrowRight,
  ArrowUp,
  ArrowWidth,
  Button,
  Text,
  Truck,
  WeighIcon,
  theme,
} from "ui";

type BlackTheme = {
  isBlackTheme?: boolean;
};

interface ProductProps extends BlackTheme {
  isClientMobile?: boolean;
  isClientTablet?: boolean;
}

interface ContainerInfoProps {
  Collapse: boolean;
}

export const Container = styled.section`
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
`;

export const SectionInfo = styled.section`
  flex: 1;
`;

export const SectionSub = styled.section<ProductProps>`
  flex: 0.3;
  @media ${theme.device.desktopFirst.tablet} {
    flex: 1;
  }

  @media ${theme.device.desktopFirst.mobileXL} {
    margin-top: 0.5rem;
  }
`;

export const ContainerInfo = styled.div<ContainerInfoProps>`
  display: flex;
  gap: 1rem;
  ${({ Collapse }) =>
    Collapse
      ? `
        display: none;
      `
      : `
        display: flex;`}

  @media ${theme.device.desktopFirst.tablet} {
    flex-direction: column;
  }
`;

export const BoxAlign = styled.div`
  display: flex;
  gap: 1rem;
`;

export const Group = styled.div<ProductProps>`
  width: 100%;
  margin-top: 0.5rem;
`;

export const LinkPDF = styled.a<ProductProps>`
  width: 100%;
  margin-top: 0.5rem;
  font-size: 1.3rem;
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday["300"]}`
      : `${theme.colors.blackFriday["480"]}`};

  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow-wrap: anywhere;
`;

export const TitleBlockTable = styled.h3<ProductProps>`
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.neutral.white}`
      : `${theme.colors.blackFriday["115"]}`};

  font-size: ${({ isClientMobile, isClientTablet }) =>
    isClientMobile ? "1.3rem" : isClientTablet ? "1.3rem" : "1.6rem"};

  padding: 1rem 0;
  font-weight: 500;
  border-bottom: thin solid
    ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["100"]}`
        : `${theme.colors.blackFriday["33"]}`};
`;

export const CellTable = styled.td<ProductProps>`
  line-height: 2rem;
  padding: 0.5rem 1rem;
  width: 50%;
  display: ${({ isClientMobile, isClientTablet }) =>
    isClientMobile ? "flex" : isClientTablet ? "flex" : ""};
  flex-direction: column;
  font-size: ${({ isClientMobile, isClientTablet }) =>
    isClientMobile ? "1.2rem" : isClientTablet ? "1.2rem" : "initial"};
`;

export const LineTable = styled.tr<ProductProps>`
  :nth-child(${({ isBlackTheme }) => (isBlackTheme ? "odd" : "even")}) {
    background-color: ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["150"]}`
        : `${theme.colors.blackFriday["5"]}`};
  }
  ${CellTable}:first-child {
    font-weight: 700;
    color: ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.neutral.white}`
        : `${theme.colors.blackFriday["115"]}`};
  }
  ${CellTable}:last-child {
    color: ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.neutral.white}`
        : `${theme.colors.blackFriday["80"]}`};
  }
`;

export const BodyTable = styled.tbody``;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.2rem;
  margin-top: 0.5rem;
`;

export const BoxSpecs = styled.section<ProductProps>`
  display: flex;
  flex-direction: column;
  background-color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday["175"]}`
      : `${theme.colors.blackFriday.white}`};
  border-radius: 2px;
  border: thin solid
    ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["100"]}`
        : `${theme.colors.blackFriday["30"]}`};
  border-radius: 8px;
  padding: ${({ isClientMobile, isClientTablet }) =>
    isClientMobile
      ? "0.1rem 1rem"
      : isClientTablet
      ? "0.1rem 1rem"
      : "2rem 1rem"};
  margin-top: ${({ isClientMobile, isClientTablet }) =>
    isClientMobile ? "-0.9rem" : isClientTablet ? "-0.9rem" : "initial"};
`;

export const BoxLinks = styled.div<ProductProps & ContainerInfoProps>`
  display: flex;
  background-color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday["175"]}`
      : `${theme.colors.blackFriday.white}`};
  border: thin solid
    ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["100"]}`
        : `${theme.colors.blackFriday["30"]}`};
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

export const BoxDimension = styled.div<ProductProps & ContainerInfoProps>`
  display: flex;
  border-radius: 2px;
  border: thin solid
    ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["100"]}`
        : `${theme.colors.blackFriday["30"]}`};
  border-radius: 8px;
  padding: 1rem;
  background-color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday["175"]}`
      : `${theme.colors.blackFriday.white}`};
`;

export const Title = styled.h2<BlackTheme>`
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday.white}`
      : `${theme.colors.neutral["520"]}`};
  margin-bottom: 1.5rem;
  font-size: ${theme.typography.fontSize.md.x3};
  font-weight: 500;
`;

export const Row = styled.div`
  display: flex;
`;

export const SubtitleDimension = styled.strong<ProductProps>`
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.neutral.white}`
      : `${theme.colors.blackFriday["115"]}`};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

export const Value = styled.span<ProductProps>`
  font-size: 1.15rem;
  font-weight: 500;
  margin-left: 0.5rem;
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.neutral.white}`
      : `${theme.colors.blackFriday.black}`};
`;

export const Margin1 = styled.div`
  margin-top: 1rem;
`;

export const AggregatorCustom = styled(Aggregator)`
  display: flex;
  padding: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const TextCustom = styled(Text)<ProductProps>`
  flex: 1;
  color: ${theme.colors.neutral["520"]};
  font-size: ${theme.typography.fontSize.md.x3};
  font-weight: ${theme.typography.fontWeight["500"]};
  color: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday.white}`
      : `${theme.colors.neutral["520"]}`};

  @media ${theme.device.desktopFirst.tablet} {
    font-size: 1.5rem;
    padding-left: ${theme.space["x2.5"]};
  }
`;

export const ButtonCustom = styled(Button)<ProductProps>`
  width: 100%;
  box-shadow: 0px 3px 6px ${theme.colors.neutral["999-opacity-10"]};
  padding: ${theme.space.x1};
  -webkit-tap-highlight-color: transparent !important;

  background: ${({ isBlackTheme }) =>
    isBlackTheme
      ? `${theme.colors.blackFriday["175"]}`
      : `${theme.colors.neutral.white}`};

  border: thin solid
    ${({ isBlackTheme }) =>
      isBlackTheme
        ? `${theme.colors.blackFriday["100"]}`
        : `${theme.colors.blackFriday["30"]}`};

  border-radius: 2px;

  &:hover {
    background-color: initial;
  }
`;

export const DimensionArrowWidth = styled(ArrowWidth)``;

export const DimensionArrowHeight = styled(Truck)``;

export const DimensionArrowLength = styled(ArrowLength)``;

export const DimensionWeightIcon = styled(WeighIcon)``;

export const PDFArrow = styled(ArrowRight)``;

export const CollapseArrowUp = styled(ArrowUp)``;

export const CollapseArrowDown = styled(ArrowDown)``;
